import { internalLogger } from '../../interface/v1/clientOsLogger';
import AuthContextEnum from '../../services/authTokenService/AuthContextEnum';
import IAuthTokenService from '../../services/authTokenService/IAuthTokenService';
import { IEventService } from '../../services/eventService';
import { EventServiceValueType } from '../../services/eventService/types';

type JWebAccessTokenChangeEventData = {
  currentAccessToken: string | null;
  state: AccessTokenChangeEventState;
};

type AccessTokenChangeEventState =
  | 'loginStart'
  | 'loginFinish'
  | 'exchangeStart'
  | 'exchangeFinish'
  | 'refreshStart'
  | 'refreshFinish'
  | 'logoutStart'
  | 'logoutFinish';

type JWebAccessTokenChangeListenerParams = {
  eventService: IEventService;
  authTokenService: IAuthTokenService;
};

export class JWebAccessTokenChangeListener {
  /**
   * Event Specification {@link https://github.azc.ext.hp.com/jarvis/shared-documentation/blob/master/native/auth/access-token-events.md}
   */
  private readonly AUTH_PLUGIN_ACCESS_TOKEN_CHANGE_EVENT =
    'com.hp.jarvis.auth.event.accesstoken.change';

  private eventService: IEventService;
  private authTokenService: IAuthTokenService;

  constructor({
    eventService,
    authTokenService
  }: JWebAccessTokenChangeListenerParams) {
    this.eventService = eventService;
    this.authTokenService = authTokenService;
  }

  public async init(): Promise<void> {
    await this.eventService.subscribe(
      this.AUTH_PLUGIN_ACCESS_TOKEN_CHANGE_EVENT,
      (e) => this.handleAccessTokenChange(e)
    );
  }

  private async handleAccessTokenChange(
    event: EventServiceValueType
  ): Promise<void> {
    internalLogger?.log?.i6({
      className: 'JWebAccessTokenChangeListener',
      message: 'Received JWeb access token change event',
      parameters: { event },
      eventType: this.AUTH_PLUGIN_ACCESS_TOKEN_CHANGE_EVENT,
      source: event?.publisherId
    });

    const eventData = event?.eventData as JWebAccessTokenChangeEventData;

    switch (eventData?.state) {
      case 'refreshFinish':
        await this.syncStoredToken(eventData);
        break;
    }
  }

  private async syncStoredToken(
    eventData: JWebAccessTokenChangeEventData
  ): Promise<void> {
    const receivedAccessToken = eventData?.currentAccessToken;

    if (!receivedAccessToken) {
      internalLogger?.error?.e1({
        className: 'JWebAccessTokenChangeListener',
        message:
          'Expected access token not provided in event data, JWeb error occurred',
        variables: { eventData },
        fallbackAction: 'Token synchronization aborted'
      });
      return;
    }

    const receivedAccessTokenTenantId =
      this.authTokenService.getTenantIdFromGivenToken(receivedAccessToken);

    const authContext = receivedAccessTokenTenantId
      ? AuthContextEnum.tenant
      : AuthContextEnum.tenantless;

    const storedAccessToken =
      this.authTokenService.getToken(authContext)?.token;

    if (storedAccessToken === receivedAccessToken) {
      internalLogger?.debug?.d0({
        className: 'JWebAccessTokenChangeListener',
        message: 'Access token matches the stored token'
      });
      return;
    }

    internalLogger?.debug?.d0({
      className: 'JWebAccessTokenChangeListener',
      message: 'Setting new access token in AuthTokenService'
    });
    this.authTokenService.setToken(eventData.currentAccessToken, authContext);
  }
}
